import { parseHHMM, type WorkCalendar } from '@hana/work-calendar';
import { LEAVE_YEAR_FROM } from './fixtures';
import type { MeeAttendance, MeePerson, MeeRequest } from './types';

const nextDay = (day: string): string => {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(Date.UTC(y!, m! - 1, d! + 1)).toISOString().slice(0, 10);
};

/** Số ngày nghỉ của một đơn: ngày làm việc trong [from, to] theo lịch công ty; nửa ngày = 0,5 (chỉ khi một ngày làm việc). */
export function leaveDays(cal: WorkCalendar, from: string, to: string = from, half = false): number {
  if (to < from) return 0;
  let n = 0;
  for (let d = from; d <= to; d = nextDay(d)) if (cal.isWorkDay(d)) n++;
  return half ? (from === to && n === 1 ? 0.5 : 0) : n;
}

/** Giờ có mặt giữa hai mốc trong ngày, trừ khoảng nghỉ giữa các ca (nghỉ trưa) theo lịch. */
export function presenceBetween(cal: WorkCalendar, day: string, a: string, b: string): number {
  if (!a || !b) return 0;
  const A = parseHHMM(a);
  const B = parseHHMM(b);
  if (B <= A) return 0;
  const shifts = cal.shiftsOf(day);
  let total = B - A;
  for (let i = 1; i < shifts.length; i++) {
    const lo = Math.max(shifts[i - 1]![1], A);
    const hi = Math.min(shifts[i]![0], B);
    total -= Math.max(0, hi - lo);
  }
  return total / 60;
}

/** Giờ tăng ca của đơn OT. */
export const otHours = (r: Pick<MeeRequest, 't0' | 't1'>): number =>
  r.t0 && r.t1 ? Math.max(0, parseHHMM(r.t1) - parseHHMM(r.t0)) / 60 : 0;

export interface LeaveBalance {
  quota: number;
  used: number;
  pending: number;
  /** còn lại, chưa trừ đơn chờ duyệt */
  left: number;
  /** còn lại nếu mọi đơn chờ được duyệt */
  leftAfter: number;
  /** ngày phép đã dùng theo tháng T1–T12 */
  byMonth: number[];
}

/** Phép năm: quỹ = AnnualLeaveDays của tblClientInfo · đã dùng = nền + đơn "Phép năm" đã duyệt từ đầu kỳ. */
export function leaveBalance(
  cal: WorkCalendar,
  person: Pick<MeePerson, 'id' | 'leaveUsedBase' | 'leaveByMonth'>,
  requests: readonly MeeRequest[],
  yearFrom = LEAVE_YEAR_FROM,
): LeaveBalance {
  const mine = requests.filter((r) => r.owner === person.id && r.type === 'leave' && r.sub === 'Phép năm');
  const approved = mine.filter((r) => r.status === 'Approved' && r.from >= yearFrom);
  const pending = mine.filter((r) => r.status === 'Pending').reduce((s, r) => s + (r.qty ?? 0), 0);
  const quota = cal.config.annualLeaveDays;
  const used = person.leaveUsedBase + approved.reduce((s, r) => s + (r.qty ?? 0), 0);
  const left = Math.max(0, quota - used);
  const byMonth = [...person.leaveByMonth];
  for (const r of approved) byMonth[Number(r.from.slice(5, 7)) - 1]! += r.qty ?? 0;
  return { quota, used, pending, left, leftAfter: Math.max(0, left - pending), byMonth };
}

export type DayStatus = 'ok' | 'short' | 'leave' | 'halfLeave' | 'holiday' | 'off' | 'future' | 'today';

export interface DayRecord {
  day: string;
  status: DayStatus;
  /** giờ chuẩn của ngày theo lịch */
  standardHours: number;
  in: string;
  out: string;
  presence: number;
  /** phút đi muộn so với đầu ca (nửa ngày nghỉ sáng: so với đầu ca chiều) */
  lateMinutes: number;
}

const hhmm = (m: number) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;

/** Trạng thái chấm công một ngày. Ngày làm việc không có dòng ghi riêng = đúng giờ mặc định theo ca (vào đầu ca, ra cuối ca). */
export function dayRecord(cal: WorkCalendar, day: string, today: string, rec?: MeeAttendance): DayRecord {
  const shifts = cal.shiftsOf(day);
  const std = cal.dayHours(day);
  const blank = { day, standardHours: std, in: '', out: '', presence: 0, lateMinutes: 0 };
  const holiday = cal.holidayOf(day);
  if (!shifts.length) return { ...blank, status: holiday ? 'holiday' : 'off' };
  if (day > today) return { ...blank, status: 'future' };
  if (rec?.leave) return { ...blank, status: 'leave', in: '—', out: '—' };
  const first = shifts[0]![0];
  const lastEnd = shifts[shifts.length - 1]![1];
  const start = rec?.half && shifts.length > 1 ? shifts[1]![0] : first;
  const a = rec ? rec.in : hhmm(first - 5);
  const b = rec ? rec.out : day === today ? '' : hhmm(lastEnd + 1);
  const presence = presenceBetween(cal, day, a, b);
  const late = a ? Math.max(0, parseHHMM(a) - start) : 0;
  const need = rec?.half ? std / 2 : std;
  const base = { ...blank, in: a, out: b, presence, lateMinutes: late };
  if (day === today && !b) return { ...base, status: 'today' };
  if (!b || late > 0 || presence < need - 0.01) return { ...base, status: 'short' };
  return { ...base, status: rec?.half ? 'halfLeave' : 'ok' };
}
